/**
 * The assessment weight ledger: the one policy every weighting total is read
 * from. Per module and per owning version, its own effective weight, the
 * weight other modules' shared assessments carry in, and how far that is from
 * the 100% limit.
 *
 * @module utils/assessment-weight
 */

import type { Module, ModuleAssessment, Programme, ProgrammeVersion } from "@/types";

import { resolveEffectiveAssessments } from "./assessment-overrides";

/** How far from 100 a total may sit and still count as 100. */
export const WEIGHT_TOLERANCE = 0.01;

/** Weight one coordinating module's shared assessment gives this module. */
export interface IncomingWeight {
  coordinatingModuleId: string;
  assessmentId: string;
  weighting: number;
}

export interface LedgerEntry {
  moduleId: string;
  /** Undefined when the programme has no versions. */
  version?: ProgrammeVersion;
  /** The module's effective assessments in this version. */
  assessments: ModuleAssessment[];
  own: number;
  incoming: IncomingWeight[];
  total: number;
  /** 100 minus the total; negative once over. */
  headroom: number;
  /** Finite and no more than 100, within the tolerance. */
  withinLimit: boolean;
}

export interface AssessmentWeightLedger {
  /** One entry per owning version, or per version when none places it. */
  entriesFor(moduleId: string): readonly LedgerEntry[];
}

export interface LedgerOptions {
  /** A module read in place of the stored one with the same id, e.g. an unsaved draft. */
  asIf?: Module;
}

export function assessmentWeight(assessment: ModuleAssessment): number {
  return Number(assessment.weighting ?? assessment.weight ?? 0);
}

/** Finite and within the tolerance of 100. */
export function isBalanced(total: number): boolean {
  return Number.isFinite(total) && Math.abs(total - 100) <= WEIGHT_TOLERANCE;
}

/** The entry whose total is furthest from 100, non-finite first. */
export function worstEntry(entries: readonly LedgerEntry[]): LedgerEntry | undefined {
  const distance = (entry: LedgerEntry) =>
    Number.isFinite(entry.total) ? Math.abs(entry.total - 100) : Number.POSITIVE_INFINITY;
  return entries.reduce<LedgerEntry | undefined>(
    (worst, entry) => (worst === undefined || distance(entry) > distance(worst) ? entry : worst),
    undefined,
  );
}

function placedIn(version: ProgrammeVersion, moduleId: string): boolean {
  return (version.stages ?? []).some(
    (stage) =>
      (stage.moduleIds ?? []).includes(moduleId) ||
      (stage.modules ?? []).some((placement) => placement.moduleId === moduleId),
  );
}

function effectiveIn(module: Module, version: ProgrammeVersion | undefined): ModuleAssessment[] {
  return version
    ? resolveEffectiveAssessments(module, version).assessments
    : (module.assessments ?? []);
}

function incomingIn(
  moduleId: string,
  modules: readonly Module[],
  version: ProgrammeVersion | undefined,
): IncomingWeight[] {
  return modules
    .filter((coordinating) => coordinating.id !== moduleId)
    .flatMap((coordinating) =>
      effectiveIn(coordinating, version).flatMap((shared) =>
        (shared.participatingModules ?? [])
          .filter((participation) => participation.moduleId === moduleId)
          .map((participation) => ({
            coordinatingModuleId: coordinating.id,
            assessmentId: shared.id,
            weighting: Number(participation.weighting ?? 0),
          })),
      ),
    );
}

function withDraft(stored: readonly Module[], asIf: Module | undefined): readonly Module[] {
  if (asIf === undefined) {
    return stored;
  }
  if (!stored.some((module) => module.id === asIf.id)) {
    return [...stored, asIf];
  }
  return stored.map((module) => (module.id === asIf.id ? asIf : module));
}

export function assessmentWeightLedger(
  programme: Pick<Programme, "modules" | "versions">,
  options: LedgerOptions = {},
): AssessmentWeightLedger {
  const modules = withDraft(programme.modules ?? [], options.asIf);
  const versions = programme.versions ?? [];
  const cache = new Map<string, LedgerEntry[]>();

  const evaluate = (module: Module): LedgerEntry[] => {
    const owning = versions.filter((version) => placedIn(version, module.id));
    const candidates = owning.length > 0 ? owning : versions;
    const contexts: (ProgrammeVersion | undefined)[] =
      candidates.length > 0 ? candidates : [undefined];
    return contexts.map((version) => {
      const assessments = effectiveIn(module, version);
      const own = assessments.reduce((sum, entry) => sum + assessmentWeight(entry), 0);
      const incoming = incomingIn(module.id, modules, version);
      const total = own + incoming.reduce((sum, entry) => sum + entry.weighting, 0);
      return {
        moduleId: module.id,
        ...(version ? { version } : {}),
        assessments,
        own,
        incoming,
        total,
        headroom: 100 - total,
        withinLimit: Number.isFinite(total) && total <= 100 + WEIGHT_TOLERANCE,
      };
    });
  };

  return {
    entriesFor(moduleId) {
      const cached = cache.get(moduleId);
      if (cached) {
        return cached;
      }
      const module = modules.find((candidate) => candidate.id === moduleId);
      const entries = module ? evaluate(module) : [];
      cache.set(moduleId, entries);
      return entries;
    },
  };
}
